import classNames from "classnames"
import React from "react"

import Typography from "@components/elements/V2/Typography"
import { Color } from "@constants/V2/color"

export type PlanConfigData = Omit<Storyblok.PlanConfig["content"], "component">

type Props = {
  plan: PlanConfigData
  accentColor: Color
}

const formatRate = (rate: number) => `${Number(rate).toFixed(2)}%`

const PlanRatesTable = ({ plan, accentColor }: Props) => {
  const dividerClassName = classNames(
    "w-full my-24",
    accentColor === Color.White ? "text-white/10" : "text-charcoal/10"
  )

  return (
    <div className="space-y-12">
      <hr className={dividerClassName} />

      <div className="flex items-center justify-between gap-8">
        <Typography
          size="subscript-sm"
          font="grotesk"
          weight="medium"
          color={accentColor}
          text="Transaction fee"
          className="text-nowrap uppercase"
        />
        <div className="flex items-center gap-8">
          <Typography
            size="subscript-sm"
            font="grotesk"
            weight="medium"
            color={accentColor}
            text="DEBIT"
            className="w-[40px] text-right uppercase opacity-60"
          />
          <Typography
            size="subscript-sm"
            font="grotesk"
            weight="medium"
            color={accentColor}
            text="CREDIT"
            className="w-[40px] text-right uppercase opacity-60"
          />
          {plan.internationalRate != null && (
            <Typography
              size="subscript-sm"
              font="grotesk"
              weight="medium"
              color={accentColor}
              text="INTL."
              className="w-[40px] text-right uppercase opacity-60"
            />
          )}
        </div>
      </div>

      <div className="space-y-8">
        <div className="space-y-8">
          <div className="flex items-center justify-between gap-8">
            <Typography
              size="body-sm"
              font="grotesk"
              weight="book"
              color={accentColor}
              text="Up to R50k"
              className="text-nowrap"
            />
            <div className="flex items-center gap-8">
              <Typography
                size="body-sm"
                font="grotesk"
                weight="medium"
                color={accentColor}
                text={formatRate(plan.standardRate)}
                className="w-[40px] text-right"
              />
              <Typography
                size="body-sm"
                font="grotesk"
                weight="medium"
                color={accentColor}
                text={formatRate(plan.creditStandardRate ?? plan.standardRate)}
                className="w-[40px] text-right"
              />
              {plan.internationalRate != null && (
                <Typography
                  size="body-sm"
                  font="grotesk"
                  weight="medium"
                  color={accentColor}
                  text={formatRate(plan.internationalRate)}
                  className="w-[40px] text-right"
                />
              )}
            </div>
          </div>
          <hr className={dividerClassName} />
        </div>

        <div className="space-y-8">
          <div className="flex items-center justify-between gap-8">
            <Typography
              size="body-sm"
              font="grotesk"
              weight="book"
              color={accentColor}
              text="R50k - R200k"
              className="text-nowrap"
            />
            <div className="flex items-center gap-8">
              <Typography
                size="body-sm"
                font="grotesk"
                weight="medium"
                color={accentColor}
                text={formatRate(plan.reducedRate ?? plan.standardRate)}
                className="w-[40px] text-right"
              />
              <Typography
                size="body-sm"
                font="grotesk"
                weight="medium"
                color={accentColor}
                text={formatRate(
                  plan.creditReducedRate ??
                    plan.creditStandardRate ??
                    plan.standardRate
                )}
                className="w-[40px] text-right"
              />
              {plan.internationalRate != null && (
                <Typography
                  size="body-sm"
                  font="grotesk"
                  weight="medium"
                  color={accentColor}
                  text={formatRate(
                    plan.internationalReducedRate ?? plan.internationalRate
                  )}
                  className="w-[40px] text-right"
                />
              )}
            </div>
          </div>
          <hr className={dividerClassName} />
        </div>

        <div className="flex items-center justify-between gap-8">
          <Typography
            size="body-sm"
            font="grotesk"
            weight="book"
            color={accentColor}
            text="R200k+"
            className="text-nowrap"
          />
          <Typography
            size="body-sm"
            font="grotesk"
            weight="medium"
            color={accentColor}
            text="Custom"
            className="text-right italic"
          />
        </div>
      </div>
    </div>
  )
}

export default PlanRatesTable
